import React, {SetStateAction, useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {ProfileMain} from 'src/components/profile-tab-structure';
import {CheckboxSkeleton, Skeleton} from 'src/components/skeleton';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContext, ValidationContextProvider} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {useIpRanges} from 'src/modules/profile/account/tabs/settings/sections/access-controls';
import {IpRangesSection} from 'src/modules/profile/account/tabs/settings/sections/access-controls/ip-ranges';
import {ResetPeriod} from 'src/openapi/admin/models/ResetPeriod';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';
import {useShown} from '@pb/kit-hooks-use-shown';

import {ApprovedDomains, useApprovedDomains} from './approved-domains';
import {
    LoginPageCustomizationSection,
    MicroSiteBaseInfoSection,
    MicroSiteHostsSection,
    useMicroSiteBaseInfo,
} from './base-info';
import {EmailSetupSection, useEmailSetup} from './email-setup';
import {RemoveMicrositeModal} from './remove-microsite-modal';
import {UserResetSettings, useUserResetSettings} from './reset-settings';

import type {IpRange} from 'src/openapi/admin/models/IpRange';

export const SECTION_NAME = 'Microsites Configurations';

type Props = {
    accountId: number,
};

const MicrositeConfigurations = ({accountId}: Props) => {
    const {canEditMicrosites, hasAccess} = useAdminAccessRight();
    const {accountSubmit, accountFieldChange} = useEventTracking();
    const {clearErrors} = useContext(ValidationContext);
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);

    const [saveBusy, setSaveBusy] = useState(false);
    const [isNewMicroSite, setIsNewMicroSite] = useState(false);
    const [emailBodyKey, setEmailBodyKey] = useState(0);
    const {
        shown: showDeleteMicrositeModal, onClick: onMicrositeDeleteClick, onClose: onCloseMicrositeDeleteModal,
    } = useShown();

    const [canSave, setCanSave] = useState(false);

    const {isCurrentChunkChanged, setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'account-microsites-tab',
        onProceed: () => {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        },
    });

    const {
        isLoading: isMicroSiteBaseInfoLoading,
        loadingError: microSiteBaseInfoLoadingError,
        isChanged: isMicroSiteBaseInfoChanged,
        data: microSiteBaseInfo,
        dataInitial: microSiteBaseInfoInitial,
        setData: setMicroSiteBaseInfo,
        saveChanges: saveMicroSiteBaseInfo,
        discardChanges: discardMicroSiteBaseInfo,
        loadData: loadMicroSiteBaseInfo,
    } = useMicroSiteBaseInfo({
        accountId,
        isNewMicroSite,
        onLoadNewMicroSite: () => setIsNewMicroSite(true),
        onCreateMicroSite: () => setIsNewMicroSite(false),
    });

    const {
        isLoading: isIpRangesLoading,
        loadingError: ipRangesLoadingError,
        isChanged: isIpRangesChanged,
        data: ipRanges,
        setData: setIpRanges,
        saveChanges: saveIpRanges,
        discardChanges: discardIpRanges,
        setLastEditedRange,
        loadData: loadIpRanges,
    } = useIpRanges({accountId});

    const {
        isLoading: isUserResetSettingsLoading,
        loadingError: userResetSettingsLoadingError,
        isChanged: isUserResetSettingsChanged,
        data: userResetSettings,
        setData: setUserResetSettings,
        saveChanges: saveUserResetSettings,
        discardChanges: discardUserResetSettings,
        loadData: loadUserResetSettings,
    } = useUserResetSettings({accountId});

    const {
        isLoading: isEmailSetupLoading,
        loadingError: emailSetupLoadingError,
        isChanged: isEmailSetupChanged,
        data: emailSetup,
        setData: setEmailSetup,
        saveChanges: saveEmailSetup,
        discardChanges: discardEmailSetup,
        loadData: loadEmailSetup,
    } = useEmailSetup({accountId});

    const {
        isLoading: isApprovedDomainsLoading,
        loadingError: approvedDomainsLoadingError,
        isChanged: isApprovedDomainsChanged,
        data: approvedDomains,
        dataInitial: initApprovedDomains,
        setData: setApprovedDomains,
        saveChanges: saveApprovedDomains,
        discardChanges: discardApprovedDomains,
        loadData: loadApprovedDomains,
    } = useApprovedDomains({accountId});

    useEffect(() => {
        setIsChunkChanged([
            isMicroSiteBaseInfoChanged,
            isEmailSetupChanged,
            isIpRangesChanged,
            isUserResetSettingsChanged,
            isApprovedDomainsChanged,
        ].some(Boolean));
    }, [
        setIsChunkChanged,
        isMicroSiteBaseInfoChanged, isEmailSetupChanged, isIpRangesChanged,
        isUserResetSettingsChanged, isApprovedDomainsChanged,
    ]);

    const trackParameterChange = useCallback((parameterName: string) => {
        accountFieldChange({
            section: SECTION_NAME,
            viewType: isNewMicroSite ? 'New' : 'Existed',
            parameterName,
        });
    }, [isNewMicroSite, accountFieldChange]);

    const saveChanges = useCallback(async () => {
        setCanSave(false);
        accountSubmit({submit: true, place: SECTION_NAME});

        const calls = [];

        setSaveBusy(true);
        if (isNewMicroSite || isMicroSiteBaseInfoChanged) {
            calls.push(await saveMicroSiteBaseInfo());
        }

        if (isIpRangesChanged) {
            calls.push(await saveIpRanges());
        }

        if (isEmailSetupChanged) {
            calls.push(await saveEmailSetup());
        }

        if (isUserResetSettingsChanged) {
            calls.push(await saveUserResetSettings());
        }

        if (isApprovedDomainsChanged) {
            calls.push(await saveApprovedDomains());
        }
        setSaveBusy(false);

        const errors = [...new Set(calls.filter(Boolean))];

        if (errors.length) {
            setErrorMessage(errors.join('\n'));
        } else {
            proceed();
        }
    }, [
        isNewMicroSite,
        isMicroSiteBaseInfoChanged,
        saveMicroSiteBaseInfo,
        isEmailSetupChanged,
        saveEmailSetup,
        isIpRangesChanged,
        saveIpRanges,
        isUserResetSettingsChanged,
        saveUserResetSettings,
        isApprovedDomainsChanged,
        saveApprovedDomains,
        accountSubmit,
        proceed,
        setErrorMessage,
    ]);


    useEffect(() => {
        if (canSave) {
            saveChanges();
        }
    }, [canSave, saveChanges]);

    const discardChanges = useCallback(() => {
        discardMicroSiteBaseInfo();
        discardEmailSetup();
        discardIpRanges();
        discardUserResetSettings();
        discardApprovedDomains();

        clearErrors();
        setEmailBodyKey(prev => prev + 1);
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
        accountSubmit({submit: false, place: SECTION_NAME});
    }, [
        discardMicroSiteBaseInfo,
        discardEmailSetup,
        discardIpRanges,
        discardUserResetSettings,
        discardApprovedDomains,
        clearErrors,
        setSuccessMessage,
        accountSubmit,
    ]);

    const canEdit = useMemo(() => canEditMicrosites(+accountId), [accountId, canEditMicrosites]);
    const canView = useMemo(() => hasAccess(ACCESS_RIGHTS.MICROSITES_SCREEN_VIEW), [hasAccess]);

    const onSuccess = useCallback(() => {
        setSuccessMessage('Microsite deleted successfully');
        onCloseMicrositeDeleteModal();
        loadApprovedDomains();
        loadMicroSiteBaseInfo();
        loadIpRanges();
        loadUserResetSettings();
        loadEmailSetup();
    }, [
        setSuccessMessage,
        onCloseMicrositeDeleteModal,
        loadApprovedDomains,
        loadMicroSiteBaseInfo,
        loadIpRanges,
        loadUserResetSettings,
        loadEmailSetup,
    ]);

    const isEnableUserCreationChecked = !microSiteBaseInfoInitial.micrositeName
        ? false
        : userResetSettings.enableCreation;

    const handleEnableUserCreationChange = useCallback((data: {event: {currentTarget: {checked: boolean}}}) => {
        const enableCreation = data.event.currentTarget.checked;
        trackParameterChange('Enable User Creation');
        setUserResetSettings((prev: typeof userResetSettings) => ({
            ...prev,
            enableCreation,
        }));
    }, [trackParameterChange, setUserResetSettings]);

    const handleIpRangesChange = useCallback((data: SetStateAction<IpRange[]>) => {
        trackParameterChange('IP Ranges');
        setIpRanges(data);
    }, [trackParameterChange, setIpRanges]);

    const isApprovedDomainLoadingError = !microSiteBaseInfoInitial.micrositeName ? null : approvedDomainsLoadingError;

    const ipRangesValue = !microSiteBaseInfoInitial.micrositeName ? [{
        start: '',
        end: '',
    }] : ipRanges;

    const userResetSettingsData = !microSiteBaseInfoInitial.micrositeName ? {
        nextResetDate: null,
        resetPeriod: ResetPeriod._3,
        enableCreation: false,
        exceptUserReset: false,
    } : userResetSettings;

    return (
        <>
            <ProfileMain header={(
                <Item grow={1}>
                    <Group justify="space-between">
                        <Typography
                            fontWeight="semi-bold"
                            variant="heading-level-3">
                            Primary Configurations
                        </Typography>
                        {canEdit ? (
                            <Group>
                                <Button
                                    variant="Type.SECONDARY"
                                    disabled={!microSiteBaseInfoInitial.micrositeName}
                                    onClick={onMicrositeDeleteClick}>
                                    Delete Microsite
                                </Button>
                                <Button
                                    variant="Type.SECONDARY"
                                    disabled={!isCurrentChunkChanged}
                                    onClick={discardChanges}>
                                    Discard Changes
                                </Button>
                                <Button
                                    busy={saveBusy}
                                    variant="Type.PRIMARY"
                                    disabled={!isCurrentChunkChanged}
                                    onClick={() => setTimeout(() => setCanSave(true), 500)}>
                                    Save Changes
                                </Button>
                            </Group>
                        ) : null}
                    </Group>
                </Item>
            )}>
                <MicroSiteBaseInfoSection
                    readonly={!canEdit}
                    isLoading={isMicroSiteBaseInfoLoading}
                    loadingError={microSiteBaseInfoLoadingError}
                    microSiteBaseInfo={microSiteBaseInfo}
                    setMicroSiteBaseInfo={setMicroSiteBaseInfo}
                    trackChange={trackParameterChange}
                />
                <Group alignItems="start" gap="xl">
                    <Group direction="v">
                        <Skeleton shown={isUserResetSettingsLoading} view={<CheckboxSkeleton labelWidth={100}/>}>
                            {canView ? (
                                <CheckBox
                                    label="Enable User Creation"
                                    disabled={!canEdit}
                                    checked={isEnableUserCreationChecked}
                                    onCheckBoxChange={handleEnableUserCreationChange}
                                />
                            ) : null}
                        </Skeleton>
                        <MicroSiteHostsSection
                            readonly={!canEdit}
                            isLoading={isMicroSiteBaseInfoLoading}
                            loadingError={microSiteBaseInfoLoadingError}
                            microSiteBaseInfo={microSiteBaseInfo}
                            initMicroSiteBaseInfo={microSiteBaseInfoInitial}
                            setMicroSiteBaseInfo={setMicroSiteBaseInfo}
                            trackChange={trackParameterChange}
                        />
                    </Group>
                    <ApprovedDomains
                        readonly={!canEdit}
                        isLoading={isApprovedDomainsLoading}
                        loadingError={isApprovedDomainLoadingError}
                        data={approvedDomains}
                        initData={initApprovedDomains}
                        setData={setApprovedDomains}
                        trackChange={trackParameterChange}
                    />
                </Group>
                <IpRangesSection
                    disabled={!canEdit}
                    isLoading={isIpRangesLoading}
                    loadingError={ipRangesLoadingError}
                    ipRanges={ipRangesValue}
                    onChange={handleIpRangesChange}
                    setLastEditedRange={setLastEditedRange}
                />
                <UserResetSettings
                    readonly={!canEdit}
                    isLoading={isUserResetSettingsLoading}
                    loadingError={userResetSettingsLoadingError}
                    data={userResetSettingsData}
                    setData={setUserResetSettings}
                    trackChange={trackParameterChange}
                />
                <LoginPageCustomizationSection
                    readonly={!canEdit}
                    isLoading={isMicroSiteBaseInfoLoading}
                    loadingError={microSiteBaseInfoLoadingError}
                    data={microSiteBaseInfo}
                    setData={setMicroSiteBaseInfo}
                    trackChange={trackParameterChange}
                />
                <EmailSetupSection
                    emailBodyKey={emailBodyKey}
                    readonly={!canEdit}
                    isLoading={isEmailSetupLoading}
                    loadingError={emailSetupLoadingError}
                    data={emailSetup}
                    setData={setEmailSetup}
                    trackChange={trackParameterChange}
                />
            </ProfileMain>
            <Dialog
                shown={showDeleteMicrositeModal}
                setShown={onCloseMicrositeDeleteModal}
                wrapperProps={{style: {width: 500}}}>
                <RemoveMicrositeModal
                    accountId={accountId}
                    onSuccess={onSuccess}
                    onClose={onCloseMicrositeDeleteModal}
                />
            </Dialog>
        </>
    );
};

export const ValidatedMicrositeConfigurations = ({accountId}: Props) => (
    <ValidationContextProvider>
        <MicrositeConfigurations accountId={accountId}/>
    </ValidationContextProvider>
);
